import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  X, 
  User, 
  CalendarDays, 
  CreditCard, 
  HelpCircle, 
  ChevronRight 
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function SideBarMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { logout } = useAuth(); 

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      if (logout) await logout();
      onClose();
      navigate("/auth/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      {/* 1. Backdrop Overlay */}
      <div
        className={`fixed inset-0 z-[100] bg-zinc-950/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={onClose}
      />

      {/* 2. Slide-in Drawer Panel */}
      <div
        className={`fixed top-0 right-0 z-[110] h-dvh w-4/5 max-w-sm bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col rounded-l-3xl ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header with iOS Status Bar / Dynamic Island Safe Area */}
        <div
          className="flex items-center justify-between px-6 pb-5 border-b border-zinc-100"
          style={{
            paddingTop: "max(1.25rem, calc(env(safe-area-inset-top, 0px) + 0.75rem))",
          }}
        >
          <h2 className="text-xl font-bold text-zinc-900 tracking-tight">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors active:scale-95 cursor-pointer"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Main Menu Items */}
        <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
          <MenuItem
            icon={User}
            label="Profile"
            onClick={() => handleNavigation("/profile")}
          />
          <MenuItem
            icon={CalendarDays}
            label="Events"
            onClick={() => handleNavigation("/events")}
          />
          <MenuItem
            icon={CreditCard}
            label="Subscription"
            onClick={() => handleNavigation("/payment")}
          />
        </div>

        {/* Footer / Utility Actions with iOS Home Bar Safe Area */}
        <div
          className="px-4 pt-4 border-t border-zinc-100 space-y-3 bg-zinc-50/50 rounded-bl-3xl"
          style={{
            paddingBottom: "max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem))",
          }}
        >
          <button
            onClick={async () => {
              if (Capacitor.isNativePlatform()) {
                await Browser.open({ url: "https://expolead.thensgroup.in/help" });
              } else {
                handleNavigation("/help");
              }
            }}
            className="flex items-center justify-between w-full p-3 rounded-2xl hover:bg-zinc-100 text-zinc-600 transition-colors active:scale-95 group cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              <span className="font-medium text-sm">Help Center</span>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
          </button>
          
          {/* 3D Realistic Red Door Logout Button */}
          <DoorLogoutButton onLogout={handleLogout} />
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------
// 3D Realistic Red Door Logout Button
// -----------------------------------------------------------------
function DoorLogoutButton({ onLogout }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);

  const handleClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);

    timeoutRef.current = setTimeout(() => {
      onLogout();
    }, 650);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isAnimating}
      aria-label="Log out"
      className={`relative w-full h-14 rounded-2xl px-5 text-white cursor-pointer select-none overflow-hidden transition-all duration-150 flex items-center justify-between border-t border-red-400/40 border-b-[4px] border-red-950 active:translate-y-[2px] active:border-b-[2px] ${
        isAnimating
          ? "bg-gradient-to-b from-red-800 to-red-950 shadow-[inset_0_3px_8px_rgba(0,0,0,0.6)]"
          : "bg-gradient-to-b from-red-600 via-red-600 to-red-700 hover:from-red-500 hover:to-red-700 shadow-[0_8px_18px_-3px_rgba(220,38,38,0.55),inset_0_1px_0_rgba(255,255,255,0.35)]"
      }`}
    >
      <div className="absolute inset-x-0 top-0 h-[45%] bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />

      <span className="relative z-10 text-sm font-extrabold tracking-wide text-white drop-shadow-[0_1.5px_2px_rgba(0,0,0,0.6)]">
        {isAnimating ? "Logged Out" : "Log out"}
      </span>

      <div className="relative z-10 w-11 h-11 rounded-xl bg-gradient-to-b from-[#180404] via-[#240606] to-[#0f0202] border border-red-950/80 shadow-[inset_0_3px_7px_rgba(0,0,0,0.85),0_1px_0_rgba(255,255,255,0.15)] flex items-center justify-center shrink-0 overflow-hidden">
        <div
          className="absolute z-10 flex items-center justify-center transition-all duration-500 ease-out"
          style={{
            transform: isAnimating ? "translateX(13px) scale(0.78)" : "translateX(-5px) scale(1)",
            opacity: isAnimating ? 0 : 1,
          }}
        >
          <svg
            className="w-4 h-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="5" r="2" fill="currentColor" />
            <path d="M12 7v6" />
            <path d="M9 10l3-1 3 1" />
            <path d="M10 17l2-4 2 4" />
          </svg>
        </div>

        <div 
          className="relative w-6 h-8 rounded-t-[3px] border-[2px] border-[#4a0d0d] bg-[#120202] ml-2 flex items-center shadow-[0_2px_4px_rgba(0,0,0,0.9),inset_0_2px_4px_rgba(0,0,0,0.8)] overflow-hidden"
          style={{ perspective: "220px" }}
        >
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-red-950 via-red-900 to-amber-950/60 transition-opacity duration-300 ${
              isAnimating ? "opacity-100" : "opacity-0"
            }`} 
          />

          <div
            className="w-full h-full bg-gradient-to-r from-[#801313] via-[#660e0e] to-[#450707] border border-red-400/30 rounded-t-[2px] relative flex flex-col justify-around py-1 px-0.5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2),2px_0_5px_rgba(0,0,0,0.6)]"
            style={{
              transformStyle: "preserve-3d",
              transformOrigin: "left center",
              transform: isAnimating ? "rotateY(-74deg)" : "rotateY(0deg)",
              transition: "transform 0.52s cubic-bezier(0.35, 0, 0.2, 1)",
            }}
          >
            <div className="w-full h-2.5 rounded-[1px] bg-[#4a0a0a] border border-black/40 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]" />
            <div className="w-full h-2.5 rounded-[1px] bg-[#4a0a0a] border border-black/40 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]" />
            <span
              className={`absolute top-1/2 right-0.5 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gradient-to-tr from-amber-600 via-amber-300 to-yellow-100 shadow-[0_1px_2px_rgba(0,0,0,0.7)] transition-opacity duration-200 ${
                isAnimating ? "opacity-0" : "opacity-100"
              }`}
            />
          </div>
        </div>
      </div>
    </button>
  );
}

function MenuItem({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-between w-full p-3 rounded-2xl hover:bg-zinc-50 active:bg-zinc-100 transition-all group cursor-pointer"
    >
      <div className="flex items-center gap-4">
        <div className="p-2.5 rounded-xl bg-zinc-100 text-zinc-600 group-hover:bg-white group-hover:text-zinc-950 group-hover:shadow-sm transition-all border border-transparent group-hover:border-zinc-200">
          <Icon className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <span className="font-semibold text-zinc-700 group-hover:text-zinc-950 transition-colors text-sm">
          {label}
        </span>
      </div>
      <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
    </button>
  );
}